import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { pharmacyApi } from "@/api/pharmacy";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PackagePlus, Loader2, Search, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeDateInputValue, validateDrugBatchExpiryInput } from "@/utils/dateUtils";

interface Drug {
  id: string;
  name: string;
  strength?: string;
  form?: string;
  unit?: string;
  tabletsPerStrip?: number | null;
}

type StockInLine = {
  lineId: string;
  drugId: string;
  batchNo: string;
  expiryDate: string;
  quantity: string;
  quantityUnit: "UNIT" | "STRIP";
  mrp: string;
  mrpUnit: "UNIT" | "STRIP";
  purchaseRate: string;
  purchaseRateUnit: "UNIT" | "STRIP";
  gstRate: string;
  discountRate: string;
  notes: string;
};

export default function PharmacyStockInPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [drugSearch, setDrugSearch] = useState("");
  const [addDrugOpen, setAddDrugOpen] = useState(false);
  const [addDrugForm, setAddDrugForm] = useState({
    name: "",
    genericName: "",
    brandName: "",
    category: "OTHER",
    form: "TABLET",
    strength: "",
    unit: "",
    tabletsPerStrip: "",
    description: "",
  });

  const [invoice, setInvoice] = useState({
    vendorId: "",
    vendorInvoiceNumber: "",
    vendorInvoiceDate: "",
  });

  const [lines, setLines] = useState<StockInLine[]>([]);

  const [headerNotes, setHeaderNotes] = useState("");
  const [invoiceTotal, setInvoiceTotal] = useState("");
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [hasDraft, setHasDraft] = useState(false);

  const draftKey = useMemo(() => {
    const hospitalId = (user as any)?.hospitalId ?? "unknown-hospital";
    const userId = (user as any)?.id ?? (user as any)?.email ?? "unknown-user";
    return `thms:pharmacy:stockin:draft:${hospitalId}:${userId}`;
  }, [user]);

  const restoreAttemptedRef = useRef(false);

  const headerComplete = Boolean(invoice.vendorId && invoice.vendorInvoiceNumber.trim());
  const computedTotal = useMemo(() => {
    let total = 0;
    for (const l of lines) {
      const qty = parseInt(l.quantity, 10);
      const pr = parseFloat(l.purchaseRate);
      const gst = l.gstRate.trim() === "" ? 0 : parseFloat(l.gstRate);
      const disc = l.discountRate.trim() === "" ? 0 : parseFloat(l.discountRate);
      if (!qty || qty <= 0 || Number.isNaN(qty)) continue;
      if (!pr || pr <= 0 || Number.isNaN(pr)) continue;
      if (Number.isNaN(gst) || gst < 0) continue;
      if (Number.isNaN(disc) || disc < 0) continue;
      const multiplier = 1 + gst / 100;
      const discountMultiplier = 1 - Math.min(100, disc) / 100;
      total += qty * pr * multiplier * discountMultiplier;
    }
    return Math.round(total * 100) / 100;
  }, [lines]);

  // ---- Draft (local) persistence ----
  const saveDraft = (opts?: { silent?: boolean }) => {
    try {
      const payload = {
        version: 1,
        savedAt: new Date().toISOString(),
        invoice,
        headerNotes,
        invoiceTotal,
        lines,
      };
      localStorage.setItem(draftKey, JSON.stringify(payload));
      setHasDraft(true);
      if (!opts?.silent) toast.success("Draft saved");
    } catch (e) {
      if (!opts?.silent) toast.error("Failed to save draft");
    }
  };

  const clearDraft = (opts?: { silent?: boolean }) => {
    localStorage.removeItem(draftKey);
    setHasDraft(false);
    if (!opts?.silent) toast.message("Draft cleared");
  };

  const restoreDraft = () => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as any;
      if (!parsed || typeof parsed !== "object") return false;
      setInvoice(parsed.invoice ?? { vendorId: "", vendorInvoiceNumber: "", vendorInvoiceDate: "" });
      setHeaderNotes(parsed.headerNotes ?? "");
      setInvoiceTotal(parsed.invoiceTotal ?? "");
      const restoredLines = Array.isArray(parsed.lines)
        ? parsed.lines.map((row: StockInLine) => ({
            ...row,
            expiryDate: normalizeDateInputValue(row.expiryDate),
          }))
        : [];
      setLines(restoredLines);
      setInvoiceFile(null); // can't restore File object
      setDrugSearch("");
      setHasDraft(true);
      toast.message("Draft restored. Please re-upload invoice file if needed.");
      return true;
    } catch {
      return false;
    }
  };

  // On mount, detect if draft exists (do not auto-restore without user action)
  useEffect(() => {
    if (restoreAttemptedRef.current) return;
    restoreAttemptedRef.current = true;
    try {
      setHasDraft(Boolean(localStorage.getItem(draftKey)));
    } catch {
      setHasDraft(false);
    }
  }, [draftKey]);

  // Auto-save draft (debounced) whenever form changes
  useEffect(() => {
    const shouldPersist = Boolean(
      invoice.vendorId ||
        invoice.vendorInvoiceNumber.trim() ||
        invoice.vendorInvoiceDate ||
        headerNotes.trim() ||
        invoiceTotal.trim() ||
        lines.length > 0
    );
    if (!shouldPersist) {
      // If user cleared everything, remove draft silently
      clearDraft({ silent: true });
      return;
    }
    const id = window.setTimeout(() => saveDraft({ silent: true }), 600);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, headerNotes, invoiceTotal, lines, draftKey]);

  const { data: vendors = [] } = useQuery({
    queryKey: ["pharmacy-vendors"],
    queryFn: async () => {
      const res = await pharmacyApi.listVendors();
      return (res.data as any)?.data ?? [];
    },
  });

  const { data: drugs = [], isLoading: drugsLoading } = useQuery({
    queryKey: ["drugs-list"],
    queryFn: async () => {
      const res = await api.get<{ data?: Drug[] }>("/api/prescription/drugs");
      return res.data?.data ?? [];
    },
  });

  const filteredDrugs = drugSearch
    ? drugs.filter(
        (d) =>
          d.name?.toLowerCase().includes(drugSearch.toLowerCase()) ||
          d.strength?.toLowerCase().includes(drugSearch.toLowerCase())
      )
    : drugs;

  const drugById = useMemo(() => {
    const map = new Map<string, Drug>();
    for (const d of drugs) map.set(d.id, d);
    return map;
  }, [drugs]);

  const addDrugLine = (drugId: string) => {
    const d = drugById.get(drugId);
    const pack = Number(d?.tabletsPerStrip ?? 0);
    const isStripCapable = (d?.form ?? "").toUpperCase() === "TABLET" && pack > 1;
    const line: StockInLine = {
      lineId: `${drugId}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      drugId,
      batchNo: "",
      expiryDate: "",
      quantity: "1",
      quantityUnit: isStripCapable ? "STRIP" : "UNIT",
      mrp: "",
      mrpUnit: isStripCapable ? "STRIP" : "UNIT",
      purchaseRate: "",
      purchaseRateUnit: isStripCapable ? "STRIP" : "UNIT",
      gstRate: "",
      discountRate: "",
      notes: "",
    };
    setLines((prev) => [line, ...prev]);
  };

  const updateLine = (lineId: string, patch: Partial<StockInLine>) => {
    setLines((prev) => prev.map((l) => (l.lineId === lineId ? { ...l, ...patch } : l)));
  };

  const removeLine = (lineId: string) => setLines((prev) => prev.filter((l) => l.lineId !== lineId));

  const createDrugMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: addDrugForm.name.trim(),
        genericName: addDrugForm.genericName.trim() || undefined,
        brandName: addDrugForm.brandName.trim() || undefined,
        category: addDrugForm.category,
        form: addDrugForm.form,
        strength: addDrugForm.strength.trim() || undefined,
        unit: addDrugForm.unit.trim(),
        tabletsPerStrip:
          addDrugForm.form === "TABLET" && addDrugForm.tabletsPerStrip.trim()
            ? parseInt(addDrugForm.tabletsPerStrip.trim(), 10)
            : undefined,
        description: addDrugForm.description.trim() || undefined,
      };
      if (!payload.name || !payload.category || !payload.form || !payload.unit) {
        throw new Error("Required fields: name, category, form, unit");
      }
      if (payload.tabletsPerStrip != null && (!Number.isFinite(payload.tabletsPerStrip) || payload.tabletsPerStrip < 1)) {
        throw new Error("Tablets per strip must be a positive integer");
      }
      return api.post("/api/prescription/drugs", payload);
    },
    onSuccess: async () => {
      toast.success("Drug added to master");
      setAddDrugOpen(false);
      setAddDrugForm({
        name: "",
        genericName: "",
        brandName: "",
        category: "OTHER",
        form: "TABLET",
        strength: "",
        unit: "",
        tabletsPerStrip: "",
        description: "",
      });
      await queryClient.invalidateQueries({ queryKey: ["drugs-list"] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || "Failed to add drug";
      toast.error(msg);
    },
  });

  const stockInMutation = useMutation({
    mutationFn: async () => {
      if (!invoice.vendorId) throw new Error("Please select a vendor");
      if (!invoice.vendorInvoiceNumber.trim()) throw new Error("Vendor invoice number is required");
      const manualTotal = parseFloat(invoiceTotal);
      if (!manualTotal || manualTotal <= 0 || Number.isNaN(manualTotal)) {
        throw new Error("Invoice total amount is required and must be > 0");
      }
      if (lines.length === 0) throw new Error("Add at least one medicine row");

      const preflightErrors: string[] = [];
      for (const l of lines) {
        const d = drugById.get(l.drugId);
        const label = d?.name ?? "Medicine";
        if (!l.batchNo.trim()) {
          preflightErrors.push(`${label}: batch number is required`);
          continue;
        }
        const expiryErr = validateDrugBatchExpiryInput(l.expiryDate);
        if (expiryErr) {
          preflightErrors.push(`${label}: ${expiryErr}`);
        }
      }
      if (preflightErrors.length > 0) {
        preflightErrors.slice(0, 5).forEach((msg) => toast.error(msg));
        if (preflightErrors.length > 5) {
          toast.error(`…and ${preflightErrors.length - 5} more row(s) need batch/expiry fixes`);
        }
        throw new Error(
          preflightErrors.length === 1
            ? preflightErrors[0]
            : `Fix batch/expiry on ${preflightErrors.length} row(s) before recording`
        );
      }

      const vendorId = invoice.vendorId;
      const vendorInvoiceNumber = invoice.vendorInvoiceNumber.trim();
      const vendorInvoiceDate = invoice.vendorInvoiceDate || undefined;
      const commonNotes = headerNotes.trim() || undefined;

      const invRes = await pharmacyApi.getStockInByInvoice({ vendorId, vendorInvoiceNumber });
      const alreadyRecorded = invRes.data?.data?.lines ?? [];
      if (alreadyRecorded.length > 0) {
        const duplicateLabels: string[] = [];
        for (const l of lines) {
          const d = drugById.get(l.drugId);
          const batchKey = l.batchNo.trim().toLowerCase();
          const hit = alreadyRecorded.find(
            (r) => r.drugId === l.drugId && (r.batchNo ?? "").trim().toLowerCase() === batchKey
          );
          if (hit) {
            duplicateLabels.push(`${d?.name ?? "Medicine"} (batch ${l.batchNo.trim()})`);
          }
        }
        if (duplicateLabels.length === lines.length) {
          throw new Error(
            `All ${lines.length} medicine row(s) for vendor invoice ${vendorInvoiceNumber} are already in inventory. Do not record again — edit within 24 hours from Stock & Batches.`
          );
        }
        if (duplicateLabels.length > 0) {
          throw new Error(
            `Already recorded for this invoice — do not add again: ${duplicateLabels.slice(0, 8).join("; ")}${
              duplicateLabels.length > 8 ? ` (+${duplicateLabels.length - 8} more)` : ""
            }. Edit within 24h from Stock & Batches if needed.`
          );
        }
        if (alreadyRecorded.length >= 1) {
          toast.message(
            `This invoice already has ${alreadyRecorded.length} stock-in line(s). Only add medicines not yet recorded for this invoice number.`
          );
        }
      }

      // Ensure invoice header is captured once in vendor invoices (reconciliation screen).
      // If it already exists, continue with stock-in under the same invoice number.
      try {
        const fd = new FormData();
        fd.append("invoiceNumber", vendorInvoiceNumber);
        fd.append("totalAmount", String(manualTotal));
        if (vendorInvoiceDate) fd.append("invoiceDate", vendorInvoiceDate);
        if (commonNotes) fd.append("notes", commonNotes);
        if (invoiceFile) fd.append("file", invoiceFile);
        await pharmacyApi.createVendorInvoice(vendorId, fd);
      } catch (e: any) {
        const status = e?.response?.status;
        if (status === 409) {
          toast.message("Invoice already exists for this vendor. Recording stock-in under the same invoice number.");
        } else {
          const msg = e?.response?.data?.message || e?.message || "Failed to create vendor invoice";
          throw new Error(msg);
        }
      }

      const results: Array<{ lineId: string; ok: boolean; message?: string }> = [];

      for (const l of lines) {
        const d = drugById.get(l.drugId);
        const pack = Number(d?.tabletsPerStrip ?? 0);
        const stripCapable = (d?.form ?? "").toUpperCase() === "TABLET" && pack > 1;
        const qty = parseInt(l.quantity, 10);
        const mrp = parseFloat(l.mrp);
        const purchaseRate = parseFloat(l.purchaseRate);
        const gstRate = l.gstRate.trim() === "" ? null : parseFloat(l.gstRate);
        const discountRate = l.discountRate.trim() === "" ? null : parseFloat(l.discountRate);

        const expiryNormalized = normalizeDateInputValue(l.expiryDate);
        if (!l.batchNo.trim() || !expiryNormalized) {
          results.push({ lineId: l.lineId, ok: false, message: `${d?.name ?? "Medicine"}: batch no & expiry required` });
          continue;
        }
        const expiryValidation = validateDrugBatchExpiryInput(expiryNormalized);
        if (expiryValidation) {
          results.push({ lineId: l.lineId, ok: false, message: `${d?.name ?? "Medicine"}: ${expiryValidation}` });
          continue;
        }
        if (!qty || qty <= 0 || Number.isNaN(qty)) {
          results.push({ lineId: l.lineId, ok: false, message: `${d?.name ?? "Medicine"}: quantity must be > 0` });
          continue;
        }
        if (!mrp || mrp <= 0 || Number.isNaN(mrp)) {
          results.push({ lineId: l.lineId, ok: false, message: `${d?.name ?? "Medicine"}: MRP must be > 0` });
          continue;
        }
        if (!purchaseRate || purchaseRate <= 0 || Number.isNaN(purchaseRate)) {
          results.push({ lineId: l.lineId, ok: false, message: `${d?.name ?? "Medicine"}: purchase rate must be > 0` });
          continue;
        }
        if (gstRate != null && (Number.isNaN(gstRate) || gstRate < 0 || gstRate > 100)) {
          results.push({ lineId: l.lineId, ok: false, message: `${d?.name ?? "Medicine"}: GST % must be between 0 and 100` });
          continue;
        }
        if (discountRate != null && (Number.isNaN(discountRate) || discountRate < 0 || discountRate > 100)) {
          results.push({ lineId: l.lineId, ok: false, message: `${d?.name ?? "Medicine"}: Discount % must be between 0 and 100` });
          continue;
        }

        try {
          const qtyToSave = stripCapable && l.quantityUnit === "STRIP" ? qty * pack : qty;
          const mrpToSave = stripCapable && l.mrpUnit === "STRIP" ? mrp / pack : mrp;
          const purchaseBasePerUnit = stripCapable && l.purchaseRateUnit === "STRIP" ? purchaseRate / pack : purchaseRate;
          const purchaseAfterGst =
            gstRate == null ? purchaseBasePerUnit : purchaseBasePerUnit * (1 + gstRate / 100);
          const purchaseToSave =
            discountRate == null ? purchaseAfterGst : purchaseAfterGst * (1 - discountRate / 100);

          await pharmacyApi.stockIn({
            drugId: l.drugId,
            batchNo: l.batchNo.trim(),
            expiryDate: expiryNormalized,
            mrp: Math.round(mrpToSave * 100) / 100,
            purchaseRate: Math.round(purchaseToSave * 100) / 100,
            gstRate: gstRate == null ? undefined : Math.round(gstRate * 100) / 100,
            quantity: qtyToSave,
            notes: [commonNotes, l.notes?.trim()].filter(Boolean).join(" · ") || undefined,
            vendorId,
            vendorInvoiceNumber,
            vendorInvoiceDate,
          });
          results.push({ lineId: l.lineId, ok: true });
        } catch (err: any) {
          const status = err?.response?.status;
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            (status === 409 ? "Already recorded for this invoice" : "Failed");
          results.push({ lineId: l.lineId, ok: false, message: `${d?.name ?? "Medicine"}: ${msg}` });
        }
      }

      const okCount = results.filter((r) => r.ok).length;
      const fail = results.filter((r) => !r.ok);

      // Refresh stock views so Stock & Batches page sees updated numbers
      queryClient.invalidateQueries({ queryKey: ["pharmacy-stock"] });
      queryClient.invalidateQueries({ queryKey: ["pharmacy-batches"] });

      if (okCount > 0 && fail.length === 0) {
        toast.success(`Stock-in recorded (${okCount} items)`);
        setLines([]);
        setHeaderNotes("");
        setInvoiceTotal("");
        setInvoiceFile(null);
        clearDraft({ silent: true });
        return { okCount, failCount: 0 };
      }
      if (okCount > 0 && fail.length > 0) {
        toast.message(`Recorded ${okCount} items, ${fail.length} failed`);
        fail.slice(0, 3).forEach((f) => toast.error(f.message || "Failed"));
        return { okCount, failCount: fail.length };
      }
      // none recorded
      const first = fail[0]?.message || "Nothing recorded";
      throw new Error(first);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || err?.message || "Failed to record stock-in";
      toast.error(msg);
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackagePlus className="h-5 w-5" />
            Stock In / Purchase
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select a drug and record incoming stock (purchase). If the batch number already exists for that
            medicine, quantity is added to the same batch; otherwise a new batch is created with a stock ledger entry.
          </p>

          {/* Invoice header */}
          <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-medium text-sm">Invoice details</h3>
                <p className="text-xs text-muted-foreground">
                  Stock-in is typically vendor-wise. Fill invoice header once, then add multiple medicines below.
                </p>
              </div>
              <div className="flex gap-2">
                {hasDraft && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => restoreDraft()}
                    disabled={stockInMutation.isPending}
                    title="Restore previously saved draft"
                  >
                    Resume draft
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => saveDraft()}
                  disabled={stockInMutation.isPending}
                  title="Save without recording stock"
                >
                  Save as draft
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setInvoice({ vendorId: "", vendorInvoiceNumber: "", vendorInvoiceDate: "" });
                    setLines([]);
                    setHeaderNotes("");
                    setInvoiceTotal("");
                    setInvoiceFile(null);
                    setDrugSearch("");
                    clearDraft({ silent: true });
                  }}
                  disabled={stockInMutation.isPending}
                >
                  New invoice
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLines([])}
                  disabled={stockInMutation.isPending || lines.length === 0}
                >
                  Clear medicines
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Vendor *</label>
                <Select
                  value={invoice.vendorId}
                  onValueChange={(v) => setInvoice((p) => ({ ...p, vendorId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v: any) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name} {v.phone ? `(${v.phone})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Invoice number *</label>
                <Input
                  value={invoice.vendorInvoiceNumber}
                  onChange={(e) => setInvoice((p) => ({ ...p, vendorInvoiceNumber: e.target.value }))}
                  placeholder="e.g. INV-12345"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Invoice date</label>
                <Input
                  type="date"
                  value={invoice.vendorInvoiceDate}
                  onChange={(e) => setInvoice((p) => ({ ...p, vendorInvoiceDate: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Invoice total amount (₹) *</label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={invoiceTotal}
                  onChange={(e) => setInvoiceTotal(e.target.value)}
                  placeholder="Total as per invoice"
                />
                <p className="text-[11px] text-muted-foreground">
                  <span className={invoiceTotal && Math.abs((parseFloat(invoiceTotal) || 0) - computedTotal) > 0.01 ? "text-amber-600" : ""}>
                    Medicine-wise total: ₹{computedTotal.toFixed(2)}
                  </span>
                </p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Upload invoice (optional)</label>
                <Input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => setInvoiceFile(e.target.files?.[0] ?? null)}
                />
                {invoiceFile ? (
                  <p className="text-[11px] text-muted-foreground">Selected: {invoiceFile.name}</p>
                ) : null}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Quick actions</label>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setInvoiceTotal(String(computedTotal))}
                    disabled={lines.length === 0 || stockInMutation.isPending}
                    title="Set invoice total to match medicine-wise total"
                  >
                    Use medicine total
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setInvoiceFile(null)}
                    disabled={!invoiceFile || stockInMutation.isPending}
                  >
                    Clear upload
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Invoice notes (optional)</label>
              <Textarea
                value={headerNotes}
                onChange={(e) => setHeaderNotes(e.target.value)}
                placeholder="Supplier remarks, transport details, etc."
                rows={2}
              />
            </div>
          </div>

          {/* Medicine selector */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="relative max-w-md w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                  placeholder={headerComplete ? "Search drug by name or strength..." : "Select vendor + invoice first"}
                value={drugSearch}
                onChange={(e) => setDrugSearch(e.target.value)}
                className="pl-9"
                  disabled={!headerComplete}
              />
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => setAddDrugOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add drug (master)
              </Button>
            </div>

            <div className={`flex flex-wrap gap-2 max-h-40 overflow-y-auto border rounded-md p-2 ${headerComplete ? "bg-muted/30" : "bg-muted/10 opacity-60"}`}>
              {drugsLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading drugs...
                </div>
              ) : filteredDrugs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No drugs found.</p>
              ) : (
                filteredDrugs.slice(0, 60).map((d) => (
                  <Button
                    key={d.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addDrugLine(d.id)}
                    disabled={!headerComplete}
                    title={!headerComplete ? "Complete invoice header first" : "Add as a row"}
                  >
                    {d.name} {d.strength && `(${d.strength})`}
                  </Button>
                ))
              )}
            </div>
          </div>

          {/* Medicine rows */}
          <div className="border rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-muted/30 border-b flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-medium text-sm">Medicines</h3>
                <p className="text-xs text-muted-foreground">Add rows medicine-wise under the same vendor invoice.</p>
              </div>
              <Button
                type="button"
                onClick={() => stockInMutation.mutate()}
                disabled={
                  !headerComplete ||
                  lines.length === 0 ||
                  stockInMutation.isPending ||
                  !invoiceTotal ||
                  Math.abs((parseFloat(invoiceTotal) || 0) - computedTotal) > 0.01
                }
              >
                {stockInMutation.isPending ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Recording...
                  </span>
                ) : (
                  `Record stock-in (${lines.length})`
                )}
              </Button>
                </div>

            {lines.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No medicines added yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2 min-w-[240px]">Medicine</th>
                      <th className="text-left p-2 min-w-[140px]">Batch no *</th>
                      <th className="text-left p-2 min-w-[140px]">Expiry *</th>
                      <th className="text-right p-2 min-w-[110px]">Qty *</th>
                      <th className="text-right p-2 min-w-[130px]">MRP (₹) *</th>
                      <th className="text-right p-2 min-w-[340px]">Purchase ex-GST (₹) / GST% / Disc% *</th>
                      <th className="text-left p-2 min-w-[220px]">Notes</th>
                      <th className="p-2 w-[60px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => {
                      const d = drugById.get(l.drugId);
                      return (
                        <tr key={l.lineId} className="border-t">
                          <td className="p-2">
                            <div className="font-medium">{d?.name ?? "—"}</div>
                            <div className="text-xs text-muted-foreground">
                              {d?.strength ? `${d.strength} ` : ""}
                              {d?.form ?? ""} {d?.unit ? `· ${d.unit}` : ""}
                              {d?.form?.toUpperCase() === "TABLET" && (d?.tabletsPerStrip ?? 0) > 1 ? ` · ${d.tabletsPerStrip} tabs/strip` : ""}
                </div>
                          </td>
                          <td className="p-2">
                            <Input value={l.batchNo} onChange={(e) => updateLine(l.lineId, { batchNo: e.target.value })} placeholder="e.g. BATCH123" />
                          </td>
                          <td className="p-2">
                            <Input
                              type="date"
                              min="2000-01-01"
                              max="2100-12-31"
                              value={normalizeDateInputValue(l.expiryDate)}
                              onChange={(e) =>
                                updateLine(l.lineId, { expiryDate: normalizeDateInputValue(e.target.value) })
                              }
                              title="Use calendar — 4-digit year (e.g. 2027-08-31). Strip MM/YY as last day of month."
                            />
                          </td>
                          <td className="p-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                  <Input
                                className="w-[120px]"
                    type="number"
                    min={1}
                    step={1}
                                value={l.quantity}
                                onChange={(e) => updateLine(l.lineId, { quantity: e.target.value })}
                              />
                              {(d?.form ?? "").toUpperCase() === "TABLET" && Number(d?.tabletsPerStrip ?? 0) > 1 && (
                                <Select value={l.quantityUnit} onValueChange={(v: any) => updateLine(l.lineId, { quantityUnit: v })}>
                                  <SelectTrigger className="w-[90px] h-9"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="STRIP">STRIP</SelectItem>
                                    <SelectItem value="UNIT">TAB</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                </div>
                          </td>
                          <td className="p-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                  <Input
                                className="w-[140px]"
                    type="number"
                    min={0}
                    step={0.01}
                                value={l.mrp}
                                onChange={(e) => updateLine(l.lineId, { mrp: e.target.value })}
                                placeholder={l.mrpUnit === "STRIP" ? "per strip" : "per tab"}
                              />
                              {(d?.form ?? "").toUpperCase() === "TABLET" && Number(d?.tabletsPerStrip ?? 0) > 1 && (
                                <Select value={l.mrpUnit} onValueChange={(v: any) => updateLine(l.lineId, { mrpUnit: v })}>
                                  <SelectTrigger className="w-[90px] h-9"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="STRIP">STRIP</SelectItem>
                                    <SelectItem value="UNIT">TAB</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                </div>
                          </td>
                          <td className="p-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                  <Input
                                className="w-[130px]"
                    type="number"
                    min={0}
                    step={0.01}
                                value={l.purchaseRate}
                                onChange={(e) => updateLine(l.lineId, { purchaseRate: e.target.value })}
                                placeholder={l.purchaseRateUnit === "STRIP" ? "ex-GST per strip" : "ex-GST per tab"}
                              />
                              {(d?.form ?? "").toUpperCase() === "TABLET" && Number(d?.tabletsPerStrip ?? 0) > 1 && (
                                <Select value={l.purchaseRateUnit} onValueChange={(v: any) => updateLine(l.lineId, { purchaseRateUnit: v })}>
                                  <SelectTrigger className="w-[90px] h-9"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="STRIP">STRIP</SelectItem>
                                    <SelectItem value="UNIT">TAB</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                  <Input
                                className="w-[90px]"
                    type="number"
                    min={0}
                                max={100}
                                step={0.01}
                                value={l.gstRate}
                                onChange={(e) => updateLine(l.lineId, { gstRate: e.target.value })}
                                placeholder="GST %"
                              />
                              <Input
                                className="w-[90px]"
                                type="number"
                                min={0}
                                max={100}
                                step={0.01}
                                value={l.discountRate}
                                onChange={(e) => updateLine(l.lineId, { discountRate: e.target.value })}
                                placeholder="Disc %"
                />
              </div>
                          </td>
                          <td className="p-2">
                            <Input value={l.notes} onChange={(e) => updateLine(l.lineId, { notes: e.target.value })} placeholder="Optional" />
                          </td>
                          <td className="p-2 text-right">
                            <Button type="button" size="sm" variant="outline" onClick={() => removeLine(l.lineId)} disabled={stockInMutation.isPending}>
                              <Trash2 className="h-4 w-4" />
                </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add drug master dialog */}
      <Dialog open={addDrugOpen} onOpenChange={setAddDrugOpen}>
        <DialogContent className="sm:max-w-lg" aria-describedby="add-drug-description">
          <DialogHeader>
            <DialogTitle>Add drug (master)</DialogTitle>
          </DialogHeader>
          <p id="add-drug-description" className="text-sm text-muted-foreground">
            Add a drug to the master list so you can record stock-in and dispense it.
          </p>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              createDrugMutation.mutate();
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Name *</Label>
                <Input value={addDrugForm.name} onChange={(e) => setAddDrugForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Paracetamol" />
              </div>
              <div className="space-y-1">
                <Label>Strength</Label>
                <Input value={addDrugForm.strength} onChange={(e) => setAddDrugForm((f) => ({ ...f, strength: e.target.value }))} placeholder="e.g. 500mg" />
              </div>
              <div className="space-y-1">
                <Label>Form *</Label>
                <Select value={addDrugForm.form} onValueChange={(v) => setAddDrugForm((f) => ({ ...f, form: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["TABLET","CAPSULE","SYRUP","INJECTION","CREAM","OINTMENT","DROPS","INHALER","PATCH","SUPPOSITORY","POWDER","GEL","LOTION","SPRAY","OTHER"].map((v) => (
                      <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Category *</Label>
                <Select value={addDrugForm.category} onValueChange={(v) => setAddDrugForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["ANTIBIOTIC","ANALGESIC","ANTI_INFLAMMATORY","CARDIOVASCULAR","RESPIRATORY","GASTROINTESTINAL","NEUROLOGICAL","PSYCHIATRIC","ENDOCRINE","DERMATOLOGICAL","OPHTHALMOLOGICAL","GYNECOLOGICAL","PEDIATRIC","EMERGENCY","VITAMIN_SUPPLEMENT","OTHER"].map((v) => (
                      <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Unit *</Label>
                <Input value={addDrugForm.unit} onChange={(e) => setAddDrugForm((f) => ({ ...f, unit: e.target.value }))} placeholder="e.g. tab / ml / vial" />
              </div>
              {addDrugForm.form === "TABLET" && (
                <div className="space-y-1">
                  <Label>Tablets per strip</Label>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={addDrugForm.tabletsPerStrip}
                    onChange={(e) => setAddDrugForm((f) => ({ ...f, tabletsPerStrip: e.target.value }))}
                    placeholder="e.g. 10"
                  />
                </div>
              )}
              <div className="space-y-1">
                <Label>Brand name</Label>
                <Input value={addDrugForm.brandName} onChange={(e) => setAddDrugForm((f) => ({ ...f, brandName: e.target.value }))} placeholder="Optional" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Description</Label>
                <Textarea value={addDrugForm.description} onChange={(e) => setAddDrugForm((f) => ({ ...f, description: e.target.value }))} rows={2} placeholder="Optional notes" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDrugOpen(false)} disabled={createDrugMutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={createDrugMutation.isPending}>
                {createDrugMutation.isPending ? "Saving..." : "Add drug"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
